import React from 'react';
import CustomModal from 'components/custom-modal/CustomModal';
import { VoidFn } from 'types/baseTypes';
import { getAmountWithCurrency } from 'utils/amountUtils';
import { CustomButton } from 'components';
import { ButtonType } from 'components/custom-button/CustomButton';
import { FormCloseIcon, RedCrossErrorIcon } from 'assets/icons';
import { getCardIcon, getCardType } from 'containers/receipts/util';
import Divider from 'components/divider/Divider';

import { ChargeCardErrorState } from '../types';

interface ChargeCardOnFileErrorProps extends Omit<ChargeCardErrorState, 'isOpen'> {
  isOpen: boolean;
  onClose: VoidFn;
  onTryAgain?: VoidFn;
  amount: number;
}

const ChargeCardOnFileError: React.FC<ChargeCardOnFileErrorProps> = ({
  isOpen,
  onClose,
  onTryAgain,
  amount,
  cardLast4,
  txnId,
  network,
  message = 'Payment Failed',
  subtitle = 'Something went wrong',
  canRetry = true
}) => {
  return (
    <CustomModal
      open={isOpen}
      closeModal={onClose}
      className="flex flex-col items-center"
      enableOutsideClick={false}
      width="w-full lg:w-[375px]">
      <div className="relative flex flex-col items-center p-6 py-10">
        <div className="absolute right-4 top-4">
          <FormCloseIcon className="h-6 w-6 cursor-pointer" onClick={onClose} />
        </div>

        <div className="mb-5 flex items-center justify-center rounded-full bg-[#F7C0C0] p-2">
          <RedCrossErrorIcon />
        </div>

        <div className="mb-1 text-center text-lg font-semibold text-error">{message || 'Payment Failed'}</div>
        <div className="mb-4 text-center text-sbase text-accentText">{subtitle || 'Something went wrong'}</div>

        {amount && <div className="text-center text-3xl font-bold text-secondary">{getAmountWithCurrency(amount)}</div>}

        <Divider variant="fullWidth" className="my-8 w-full border-secondaryBtn" />

        <div className="mb-8 flex items-center gap-2">
          {network && <img src={getCardIcon(network)} alt={network} className="h-6 w-6" />}
          <span className="text-sbase font-semibold text-tertiaryText">
            {network && getCardType(network)} {cardLast4}
          </span>
        </div>

        {txnId && <div className="mb-8 text-px13 text-accentText">#{txnId}</div>}

        <div className="flex w-full flex-col gap-3">
          <CustomButton
            id="error-try-again-btn"
            onClick={canRetry ? onTryAgain : onClose}
            className="w-full"
            type={ButtonType.PRIMARY}>
            {canRetry ? 'Try again' : 'Close'}
          </CustomButton>
          {canRetry && (
            <CustomButton id="error-close-btn" onClick={onClose} type={ButtonType.GHOST} className="w-full">
              Cancel
            </CustomButton>
          )}
        </div>
      </div>
    </CustomModal>
  );
};

export default ChargeCardOnFileError;
